import type { IntroSplashSpec } from "@slot/game-shell";
/**
 * Game-specific configuration for the branded intro splash. Read once by
 * `BrandedIntroSplashPresenter` at scene boot to populate the dynamic
 * pieces of the splash (volatility pips, headline badge) and by `startGame`
 * to build the cards.
 *
 * Type definition lives in shell at
 * `packages/game-shell/src/intro/IntroSplashSpec.ts` so the shell-side
 * presenter can reference it. This file holds only the per-game value.
 *
 * Keep `volatilityRating` in sync with the info-page volatility copy
 * (`info.volatility.body` in `game.<locale>.json`) — pre-production-tasks
 * tracks a verification entry to catch drift between the splash pips and
 * the info-page text.
 */


/**
 * Concrete spec for this slot. Game authors edit this file when:
 * - The game's volatility rating changes (math model rebalance).
 * - The marquee feature is enabled or removed.
 * - The intro cards change (every game has its own cards; the
 *   prefab authored them on the `Card1..3` nodes with an EMPTY icon binding).
 *
 * The card strings live in `assets-src/game-shared-visuals/localization/game.<locale>.json`
 * under the `info.intro.cardN.*` namespace; the fallbacks below mirror the English copy.
 */
export const INTRO_SPLASH_SPEC: IntroSplashSpec = {
  // 3 / 5, "Medium" (`info.volatility.body`): slot-backend-platform measured the current math at a CV of 8.1 per natural
  // spin, the lowest of the three backed titles, and recommends medium (2026-11-02; the user confirms the label).
  volatilityRating: 3,

  headlineBadgeAssetPath: null,

  // The game's three features, each shown by its own symbol from the symbols atlas.
  cards: [
    {
      icon: { sheet: "symbols/atlases/symbols", frame: "cannon_round" },
      titleKey: "info.intro.card1.title",
      titleFallback: "THE CANNON",
      bodyKey: "info.intro.card1.body",
      bodyFallback: "Every winning tumble fires the cannon: ×2, ×3 and on up to ×20 on your wins.",
    },
    {
      icon: { sheet: "symbols/atlases/symbols", frame: "FS" },
      titleKey: "info.intro.card2.title",
      titleFallback: "FREE SPINS",
      bodyKey: "info.intro.card2.body",
      bodyFallback: "3 or more FS award 10 free spins, and the cannon keeps climbing through them.",
    },
    {
      icon: { sheet: "symbols/atlases/symbols", frame: "CH" },
      titleKey: "info.intro.card3.title",
      titleFallback: "CHANCE",
      bodyKey: "info.intro.card3.body",
      bodyFallback: "No win? A Chance barrel blasts the symbols around it for another chance.",
    },
  ],
};
